import { resolveErrors } from 'app/utils/form_errors'
import { fetch, create, update } from 'app/utils/data'

function settingsFromAttributes(attrs) {
  return {
    enabled: !!attrs.enabled,
    contestEnabled: !!attrs['contest-enabled'],
    submissionEnabled: !!attrs['submission-enabled'],
    displayWinners: !!attrs['display-winners'],
    submissionCap: attrs['submission-cap'] ?? null,
    contestStartDate: attrs['contest-start-date'] ?? null,
    contestEndDate: attrs['contest-end-date'] ?? null,
    winnerAnnouncementDate: attrs['winner-announcement-date'] ?? null,
    updatedAt: attrs['updated-at'] ?? null,
    updatedByEmail: attrs['updated-by-email'] ?? null
  }
}

export default {
  namespaced: true,

  state: {
    pendingQueue: [],
    submissionCounts: null,
    settings: null
  },

  getters: {
    pendingQueue: (state) => state.pendingQueue,
    submissionCounts: (state) => state.submissionCounts,
    settings: (state) => state.settings
  },

  mutations: {
    pendingFetched(state, queue) {
      state.pendingQueue = queue
    },
    moderationApplied(state, submission) {
      state.pendingQueue = state.pendingQueue.filter((s) => s.id !== submission.id)
    },
    highlightToggled(state, { id, highlighted }) {
      // Highlighting doesn't change a submission's status, so the row stays
      // in `pendingQueue` regardless of the active filter — update in place
      // rather than removing/re-adding it.
      state.pendingQueue = state.pendingQueue.map((s) =>
        s.id === id ? { ...s, highlighted } : s
      )
    },
    winnerToggled(state, { id, winner, winnerCategory }) {
      // Same shape as highlightToggled, and for the same reason: winning is a
      // curation flag, not a status, so the row keeps its place in the queue.
      state.pendingQueue = state.pendingQueue.map((s) =>
        s.id === id ? { ...s, winner, winnerCategory } : s
      )
    },
    winnerCategorySet(state, { id, winnerCategory }) {
      state.pendingQueue = state.pendingQueue.map((s) =>
        s.id === id ? { ...s, winnerCategory } : s
      )
    },
    submissionCountsFetched(state, counts) {
      state.submissionCounts = counts
    },
    settingsFetched(state, settings) {
      state.settings = settings
    },
    settingsUpdated(state, settings) {
      state.settings = settings
    }
  },

  actions: {
    async fetchPendingQueue({ commit }, { status = 'pending', sort = 'inserted_at' } = {}) {
      const url =
        `/api/admin/icon-gallery/submissions` +
        `?status=${encodeURIComponent(status)}` +
        `&sort=${encodeURIComponent(sort)}`
      const response = await fetch({ url, type: 'icon-gallery-submission' })
      const rows = response?.data?.data ?? []
      const queue = rows.map((row) => ({
        id: row.id,
        name: row.attributes.name,
        status: row.attributes.status,
        highlighted: !!row.attributes.highlighted,
        winner: !!row.attributes.winner,
        winnerCategory: row.attributes['winner-category'] ?? null,
        width: row.attributes.width,
        height: row.attributes.height,
        path: row.attributes.path,
        pathSecondary: row.attributes['path-secondary'] ?? null,
        socialPlatform: row.attributes['social-platform'],
        socialHandle: row.attributes['social-handle'],
        insertedAt: row.attributes['inserted-at'],
        submitterAccountId: row.attributes['submitter-account-id'],
        submitterEmail: row.attributes['submitter-email'],
        submitterName: row.attributes['submitter-name']
      }))
      commit('pendingFetched', queue)
      return queue
    },

    async fetchSubmissionCounts({ commit }) {
      const response = await fetch({
        url: '/api/admin/icon-gallery/submissions/counts',
        type: 'icon-gallery-submission-counts'
      })
      const attrs = response?.data?.data?.attributes ?? {}
      const counts = {
        pending: attrs.pending ?? 0,
        approved: attrs.approved ?? 0,
        rejected: attrs.rejected ?? 0,
        highlighted: attrs.highlighted ?? 0,
        // This endpoint hand-rolls its JSON rather than going through the
        // serializer, so its attributes arrive exactly as named server-side.
        userSubmissions: attrs.user_submissions ?? 0,
        total: attrs.total ?? 0
      }
      commit('submissionCountsFetched', counts)
      return counts
    },

    async moderate({ commit }, { id, action }) {
      const response = await create({
        url: `/api/admin/icon-gallery/submissions/${id}/moderate`,
        type: 'moderation-event',
        data: { action }
      })
      const row = response?.data?.data
      const submission = {
        id: row.id,
        name: row.attributes.name,
        status: row.attributes.status
      }
      commit('moderationApplied', submission)
      return submission
    },

    async setHighlighted({ commit }, { id, highlighted }) {
      const response = await update({
        url: `/api/admin/icon-gallery/submissions/${id}/highlight`,
        patch: true,
        type: 'icon-gallery-submission',
        changes: { highlighted }
      })
      const row = response?.data?.data
      const result = {
        id: row.id,
        highlighted: !!row.attributes.highlighted
      }
      // Commit the local-state update immediately so the row reflects the
      // new value regardless of whether a follow-up refresh/counts fetch
      // succeeds. Matches the moderationApplied pattern.
      commit('highlightToggled', result)
      return result
    },

    async setWinner({ commit }, { id, winner }) {
      const response = await update({
        url: `/api/admin/icon-gallery/submissions/${id}/winner`,
        patch: true,
        type: 'icon-gallery-submission',
        changes: { winner }
      })
      const row = response?.data?.data
      const result = {
        id: row.id,
        winner: !!row.attributes.winner,
        // The server drops the category when a winner is unmarked, so read it
        // back rather than assuming the row keeps what it had.
        winnerCategory: row.attributes['winner-category'] ?? null
      }
      // Commit immediately for the same reason setHighlighted does: the row
      // should reflect the new value whether or not a follow-up refresh lands.
      commit('winnerToggled', result)
      return result
    },

    async setWinnerCategory({ commit }, { id, winnerCategory }) {
      const response = await update({
        url: `/api/admin/icon-gallery/submissions/${id}/winner-category`,
        patch: true,
        type: 'icon-gallery-submission',
        changes: { winnerCategory }
      })
      const row = response?.data?.data
      const result = {
        id: row.id,
        winnerCategory: row.attributes['winner-category'] ?? null
      }
      commit('winnerCategorySet', result)
      return result
    },

    // Admin import: create a new submission from an uploaded SVG. The row
    // lands `pending` (attributed to the admin, server-side) and shows up in
    // the queue on the caller's follow-up refresh, so no local mutation here.
    // Throws FormErrors on validation failure for the modal to surface.
    async importSubmission(_ctx, { name, width, height, path }) {
      try {
        const response = await create({
          url: '/api/admin/icon-gallery/submissions',
          type: 'icon-gallery-submission',
          data: { name, width, height, path }
        })
        const row = response?.data?.data
        return {
          id: row.id,
          name: row.attributes.name,
          status: row.attributes.status
        }
      } catch (e) {
        throw resolveErrors(e)
      }
    },

    async fetchSettings({ commit }) {
      const response = await fetch({
        url: '/api/admin/icon-gallery/settings',
        type: 'icon-gallery-settings'
      })
      const settings = settingsFromAttributes(response?.data?.data?.attributes ?? {})
      commit('settingsFetched', settings)
      return settings
    },

    async updateSettings({ commit }, changes) {
      try {
        const response = await update({
          url: '/api/admin/icon-gallery/settings',
          patch: true,
          type: 'icon-gallery-settings',
          changes
        })
        const settings = settingsFromAttributes(response?.data?.data?.attributes ?? {})
        commit('settingsUpdated', settings)
        return settings
      } catch (e) {
        throw resolveErrors(e)
      }
    }
  }
}
